/* ==========================================================================
   YUMMY GUMMY — cart upsell "+ Add" (event-delegated, survives re-renders)
   --------------------------------------------------------------------------
   The cart drawer replaces #CartDrawer innerHTML on every change, so direct
   listeners would be lost. We delegate on document instead. On add we re-use
   Dawn's cart drawer rendering (renderContents) and open the drawer.
   ========================================================================== */
(function () {
  'use strict';

  function getCart() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  /* ------------------------------------------------------------------------
     Floating overlay close (X) that follows the cursor over the dimmed page
     area while the cart drawer is open (like the reference). We track the
     pointer on the overlay, write its position to CSS vars the button reads,
     and flag the body so the button fades in only over the overlay zone.
     Event-delegated so it survives the drawer's innerHTML re-renders.
     ------------------------------------------------------------------------ */
  var ygOverlayRaf = null;
  document.addEventListener('mousemove', function (e) {
    var overlay = e.target.closest && e.target.closest('#CartDrawer-Overlay');
    if (!overlay) {
      if (document.body.classList.contains('yg-cart-overlay-hover')) {
        document.body.classList.remove('yg-cart-overlay-hover');
      }
      return;
    }
    document.body.classList.add('yg-cart-overlay-hover');
    var x = e.clientX;
    var y = e.clientY;
    if (ygOverlayRaf) return;
    ygOverlayRaf = requestAnimationFrame(function () {
      ygOverlayRaf = null;
      document.documentElement.style.setProperty('--yg-cursor-x', x + 'px');
      document.documentElement.style.setProperty('--yg-cursor-y', y + 'px');
    });
  });

  // Clear the hover flag whenever the drawer closes, so the X doesn't linger.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-yg-overlay-close], #CartDrawer-Overlay, .drawer__close')) {
      document.body.classList.remove('yg-cart-overlay-hover');
    }
  });

  /* ------------------------------------------------------------------------
     "Buy it Again" — remembers products the shopper added then removed, in
     localStorage, so the empty-cart view can offer them back. Merges with
     the server-rendered purchased list (customer.orders) and tops up from a
     fallback collection, de-duped and capped. Guests get the removed-items
     memory; logged-in customers also get real purchase history.
     ------------------------------------------------------------------------ */
  var YG_BUY_AGAIN_KEY = 'yg_buy_again_removed';
  var YG_BUY_AGAIN_MAX_STORED = 8;

  function ygReadRemoved() {
    try {
      var raw = window.localStorage.getItem(YG_BUY_AGAIN_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (err) {
      return [];
    }
  }

  function ygWriteRemoved(list) {
    try {
      window.localStorage.setItem(YG_BUY_AGAIN_KEY, JSON.stringify(list.slice(0, YG_BUY_AGAIN_MAX_STORED)));
    } catch (err) {
      /* storage full / disabled — non-fatal, feature just degrades */
    }
  }

  function ygRememberRemovedProduct(row) {
    if (!row) return;
    var nameLink = row.querySelector('.cart-item__name');
    var img = row.querySelector('.cart-item__image');
    var removeBtn = row.querySelector('.cart-remove-button');
    var url = nameLink ? nameLink.getAttribute('href') : '';
    if (!url) return;
    // Derive the product handle from /products/<handle>(?...) so we can
    // de-dupe against the purchased list (which also keys on handle).
    var handleMatch = url.match(/\/products\/([^/?#]+)/);
    var handle = handleMatch ? handleMatch[1] : url;
    var entry = {
      handle: handle,
      title: nameLink ? nameLink.textContent.trim() : handle,
      url: url.split('?')[0],
      image: img ? (img.currentSrc || img.src || '') : '',
      variant_id: removeBtn ? removeBtn.getAttribute('data-variant-id') : '',
      desc: ''
    };
    var list = ygReadRemoved().filter(function (p) { return p.handle !== handle; });
    list.unshift(entry); // most-recent first
    ygWriteRemoved(list);
  }

  function ygBuyAgainItemHTML(p) {
    var media = p.image
      ? '<img src="' + p.image + '" alt="" width="70" height="70" loading="lazy">'
      : '';
    var desc = p.desc ? '<span class="yg-cart-buy-again__desc">' + p.desc + '</span>' : '';
    var addLabel = p.price ? 'Add - ' + p.price : 'Add';
    return (
      '<li class="yg-cart-buy-again__item" data-handle="' + p.handle + '">' +
        '<a href="' + p.url + '" class="yg-cart-buy-again__media">' + media + '</a>' +
        '<div class="yg-cart-buy-again__meta">' +
          '<a href="' + p.url + '" class="yg-cart-buy-again__name">' + p.title + '</a>' +
          desc +
        '</div>' +
        (p.variant_id
          ? '<button type="button" class="yg-cart-buy-again__add" data-yg-cart-add data-variant-id="' + p.variant_id + '" aria-label="Add ' + p.title + ' to cart">' + addLabel + '</button>'
          : '') +
      '</li>'
    );
  }

  // Populate the empty-cart "Buy it Again" list: keep the server-rendered
  // purchased items, prepend the removed-from-cart memory, then top up from
  // the fallback collection — de-duped by handle, capped at the limit.
  function ygPopulateBuyAgain() {
    var wrap = document.querySelector('cart-drawer [data-yg-buy-again]');
    if (!wrap) return;
    var list = wrap.querySelector('[data-yg-buy-again-list]');
    if (!list) return;

    var limitEl = wrap.querySelector('[data-yg-buy-again-limit]');
    var limit = limitEl ? parseInt(limitEl.getAttribute('data-yg-buy-again-limit'), 10) || 4 : 4;

    var seen = {};
    // Server-rendered purchased items already in the DOM take priority.
    Array.prototype.forEach.call(list.querySelectorAll('.yg-cart-buy-again__item'), function (li) {
      var h = li.getAttribute('data-handle');
      if (h) seen[h] = true;
    });
    var count = Object.keys(seen).length;

    // Removed-from-cart memory (most recent first).
    ygReadRemoved().forEach(function (p) {
      if (count >= limit || !p.handle || seen[p.handle]) return;
      seen[p.handle] = true;
      count++;
      list.insertAdjacentHTML('afterbegin', ygBuyAgainItemHTML(p));
    });

    // Fallback collection JSON, to fill any remaining slots.
    if (count < limit) {
      var fbScript = wrap.querySelector('[data-yg-buy-again-fallback]');
      var fallback = [];
      if (fbScript) {
        try { fallback = JSON.parse(fbScript.textContent) || []; } catch (err) { fallback = []; }
      }
      fallback.forEach(function (p) {
        if (count >= limit || !p.handle || seen[p.handle]) return;
        seen[p.handle] = true;
        count++;
        list.insertAdjacentHTML('beforeend', ygBuyAgainItemHTML(p));
      });
    }

    if (count > 0) wrap.hidden = false;
  }

  // Run on load and whenever the drawer re-renders to the empty state.
  document.addEventListener('DOMContentLoaded', ygPopulateBuyAgain);
  if (document.readyState !== 'loading') ygPopulateBuyAgain();

  /* ------------------------------------------------------------------------
     Animated money counter. After each cart change the drawer re-renders the
     subtotal to its new value; we roll the displayed number from the previous
     total up/down to the new one so amounts feel live. Shopify's money format
     (window.YGConfig.moneyFormat) is reused so the currency/symbol/decimals
     always match the theme — never hand-formatted. Respects reduced motion.
     ------------------------------------------------------------------------ */
  var YG_MONEY_FMT = (window.YGConfig && window.YGConfig.moneyFormat) || '${{amount}}';
  var ygLastTotal = null;

  function ygFormatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Match the store-wide display rule: drop a ".00" (whole-rupee) decimal,
    // but keep a real non-zero fractional part (e.g. .50). This mirrors
    // yg-money-nodecimals.js so the rolling counter and static prices agree.
    var withCommas = parts[1] === '00' ? whole : whole + '.' + parts[1];
    var noDecimals = whole;
    return YG_MONEY_FMT
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
  }

  function ygAnimateMoney(el, fromCents, toCents) {
    if (!el) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || fromCents === toCents || fromCents == null) {
      el.textContent = ygFormatMoney(toCents);
      return;
    }
    // Cancel any in-flight animation on this element.
    if (el.__ygRaf) cancelAnimationFrame(el.__ygRaf);
    var duration = 500;
    var startTime = null;
    function step(ts) {
      if (startTime === null) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      // easeOutCubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(fromCents + (toCents - fromCents) * eased);
      el.textContent = ygFormatMoney(current);
      if (progress < 1) {
        el.__ygRaf = requestAnimationFrame(step);
      } else {
        el.textContent = ygFormatMoney(toCents);
        el.__ygRaf = null;
      }
    }
    el.classList.add('yg-money-rolling');
    el.__ygRaf = requestAnimationFrame(step);
    window.setTimeout(function () { el.classList.remove('yg-money-rolling'); }, duration + 60);
  }

  // Roll the drawer subtotal from the previous total to `total`. This fires
  // for EVERY cart mutation — add, quantity +/-, remove, and plan change —
  // because they all publish PUB_SUB_EVENTS.cartUpdate with cartData. We
  // defer to the next frame so the section re-render (which replaces the
  // subtotal node) has settled, then animate the FRESH node. Without the
  // rAF the node we grab can be the one that's about to be thrown away by
  // Dawn's innerHTML swap, so its rolling text never shows.
  function ygRollSubtotal(total) {
    if (typeof total !== 'number') return;
    var from = ygLastTotal;
    ygLastTotal = total;
    requestAnimationFrame(function () {
      var el = document.querySelector('cart-drawer .totals__total-value');
      ygAnimateMoney(el, from, total);
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var total = event && event.cartData && typeof event.cartData.total_price === 'number'
        ? event.cartData.total_price
        : null;
      ygRollSubtotal(total);
      // When the cart empties out, the drawer re-renders to the empty state
      // (fresh "Buy it Again" markup) — repopulate it from history/fallback.
      if (event && event.cartData && event.cartData.item_count === 0) {
        requestAnimationFrame(ygPopulateBuyAgain);
      }
      // Drawer re-rendered: hide slider products now in the cart and (re)start
      // the looping autoplay on the fresh rail.
      requestAnimationFrame(function () {
        ygFilterRecsInCart(event && event.cartData ? event.cartData : null);
        ygInitRecsUI();
        ygInitRecsAutoplay();
      });
    });

    // Seed the starting total from the current cart so the first change
    // animates from the real amount, not from zero.
    var cartUrl = (typeof routes !== 'undefined' && routes && routes.cart_url) || '/cart';
    fetch(cartUrl + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data && typeof data.total_price === 'number') ygLastTotal = data.total_price; })
      .catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-yg-cart-add], cart-drawer [data-pdp-cs-add]');
    if (!btn) return;
    e.preventDefault();

    var id = btn.getAttribute('data-variant-id');
    if (!id || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var cart = getCart();
    var body = { items: [{ id: id, quantity: 1 }] };
    if (cart && cart.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }

    fetch((window.routes ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) {
          // error (e.g. sold out) — fall back to cart page
          window.location = window.routes ? window.routes.cart_url : '/cart';
          return;
        }
        if (cart && cart.renderContents && res.sections) {
          cart.renderContents(res);
          fetch((window.routes ? window.routes.cart_url : '/cart') + '.json')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cartData) {
              if (window.YGUpdateCartCount && cartData) window.YGUpdateCartCount(cartData.item_count);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && cartData) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'yg-cart', cartData: cartData });
              }
            })
            .catch(function () {});
          if (typeof cart.open === 'function') cart.open();
        } else {
          window.location = window.routes ? window.routes.cart_url : '/cart';
        }
      })
      .catch(function () {
        window.location = window.routes ? window.routes.cart_url : '/cart';
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  });

  /* Upsell rail arrows — event-delegated (same reason as the "+ Add"
     handler above: the drawer replaces #CartDrawer innerHTML on every
     change, so a direct listener bound once would be lost on re-render). */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-yg-cart-upsell-prev], [data-yg-cart-upsell-next]');
    if (!btn) return;
    var rail = btn.closest('.yg-cart-upsell').querySelector('[data-yg-cart-upsell-rail]');
    if (!rail) return;
    var dir = btn.hasAttribute('data-yg-cart-upsell-prev') ? -1 : 1;
    var card = rail.querySelector('.yg-cart-upsell__slide');
    var step = card ? card.getBoundingClientRect().width + 12 : 200;
    rail.scrollBy({ left: dir * step, behavior: 'smooth' });
  });

  /* "You may also like" slider — LOOPING + AUTO-ADVANCE.
     One slide per step; wraps around at both ends (next past the last jumps
     back to the first, prev before the first jumps to the last). An
     auto-advance timer nudges it forward on an interval, pausing while the
     shopper hovers/focuses/touches the rail, and resetting after a manual
     arrow click. Re-initialised whenever the drawer re-renders (the innerHTML
     swap replaces the rail element), via ygInitRecsAutoplay(). */
  var YG_RECS_INTERVAL = 3500;

  function ygRecsStep(rail) {
    var slide = rail.querySelector('.yg-cart-recs__slide');
    return slide ? slide.getBoundingClientRect().width + 12 : rail.clientWidth;
  }

  // Update the scroll-progress bar fill and the prev-arrow disabled state to
  // match the rail's current scroll position.
  function ygRecsUpdateUI(rail) {
    var recs = rail.closest('[data-yg-cart-recs]');
    if (!recs) return;
    var loopW = rail.__ygLoopWidth;
    // In loop mode the bar cycles over one original set; otherwise it tracks
    // the single-page scroll range.
    var denom = loopW ? loopW : (rail.scrollWidth - rail.clientWidth);
    var ratio = denom > 0 ? (rail.scrollLeft % (loopW || (denom + rail.clientWidth))) / denom : 0;
    var fill = recs.querySelector('[data-yg-cart-recs-progress-fill]');
    if (fill) {
      var pageFrac = rail.clientWidth / ((loopW ? loopW + rail.clientWidth : rail.scrollWidth) || 1);
      var pct = Math.max(pageFrac, ratio) * 100;
      pct = Math.min(100, pct);
      fill.style.setProperty('--yg-recs-progress', pct + '%');
      fill.style.width = pct + '%';
    }
    // With a seamless loop there is no "start", so the prev arrow is always
    // enabled; only the non-looping fallback disables it at the start.
    var prev = recs.querySelector('[data-yg-cart-recs-prev]');
    if (prev) prev.disabled = !loopW && rail.scrollLeft <= 2;
  }

  /* Seamless infinite loop. We clone the (visible) slides once and append the
     copies after the originals. Advancing just scrollBy()s one step; a scroll
     listener silently rewinds by the width of the original set the moment we
     pass into the clones, so the rail appears to flow forever with no jump
     back to the start. ygSetupRecsLoop() (re)builds the clones for the
     current visible set; call it after cart-filtering changes which slides
     are shown. */
  function ygVisibleSlides(rail) {
    return Array.prototype.filter.call(
      rail.querySelectorAll('.yg-cart-recs__slide:not([data-clone])'),
      function (s) { return !s.hidden; }
    );
  }

  function ygSetupRecsLoop(rail) {
    // Remove any stale clones first.
    Array.prototype.forEach.call(rail.querySelectorAll('[data-clone]'), function (c) {
      c.parentNode.removeChild(c);
    });
    var originals = ygVisibleSlides(rail);
    // Need at least 2 slides (and real overflow) for a loop to make sense.
    if (originals.length < 2) {
      rail.__ygLoopWidth = 0;
      return;
    }
    originals.forEach(function (s) {
      var clone = s.cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.hidden = false;
      rail.appendChild(clone);
    });
    // Width of the ORIGINAL set = one full loop distance.
    rail.__ygLoopWidth = rail.scrollWidth / 2;

    if (!rail.__ygLoopScrollBound) {
      rail.__ygLoopScrollBound = true;
      rail.addEventListener('scroll', function () {
        var loopW = rail.__ygLoopWidth;
        if (!loopW) return;
        // Passed into the clones → rewind by one set (instant, seamless).
        if (rail.scrollLeft >= loopW) {
          rail.__ygSuppressSmooth = true;
          rail.scrollLeft -= loopW;
          rail.__ygSuppressSmooth = false;
        } else if (rail.scrollLeft < 0) {
          rail.scrollLeft += loopW;
        }
      }, { passive: true });
    }
  }

  function ygRecsGo(rail, dir) {
    var step = ygRecsStep(rail);
    if (rail.__ygLoopWidth) {
      // Looping rail: just nudge; the scroll listener handles the wrap.
      rail.scrollBy({ left: dir * step, behavior: 'smooth' });
      return;
    }
    // Non-looping fallback (single page): clamp at the ends.
    var maxLeft = rail.scrollWidth - rail.clientWidth;
    var atEnd = rail.scrollLeft >= maxLeft - 2;
    var atStart = rail.scrollLeft <= 2;
    if (dir > 0 && atEnd) {
      rail.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (dir < 0 && atStart) {
      rail.scrollTo({ left: maxLeft, behavior: 'smooth' });
    } else {
      rail.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-yg-cart-recs-prev], [data-yg-cart-recs-next]');
    if (!btn) return;
    var recs = btn.closest('[data-yg-cart-recs]');
    var rail = recs && recs.querySelector('[data-yg-cart-recs-rail]');
    if (!rail) return;
    var dir = btn.hasAttribute('data-yg-cart-recs-prev') ? -1 : 1;
    ygRecsGo(rail, dir);
    // Restart the autoplay clock so it doesn't fire right after a manual move.
    if (rail.__ygAutoplayRestart) rail.__ygAutoplayRestart();
  });

  // Bind the progress bar / prev-arrow state to the rail's scroll — runs even
  // when there's no overflow (so the bar reflects a single-page state), and is
  // idempotent per rail node.
  function ygInitRecsUI() {
    var recs = document.querySelector('cart-drawer [data-yg-cart-recs]');
    var rail = recs && recs.querySelector('[data-yg-cart-recs-rail]');
    if (!rail || rail.__ygUIBound) return;
    rail.__ygUIBound = true;
    rail.addEventListener('scroll', function () {
      if (rail.__ygUIRaf) return;
      rail.__ygUIRaf = requestAnimationFrame(function () {
        rail.__ygUIRaf = null;
        ygRecsUpdateUI(rail);
      });
    }, { passive: true });
    ygRecsUpdateUI(rail);
  }

  function ygInitRecsAutoplay() {
    var recs = document.querySelector('cart-drawer [data-yg-cart-recs]');
    var rail = recs && recs.querySelector('[data-yg-cart-recs-rail]');
    if (!rail) return;
    // Build the seamless-loop clones for the current visible set.
    ygSetupRecsLoop(rail);
    if (rail.__ygAutoplayBound) return;
    // No loop possible (0/1 visible slide) → nothing to autoplay.
    if (!rail.__ygLoopWidth) return;
    rail.__ygAutoplayBound = true;

    var timer = null;
    var paused = false;
    function tick() {
      if (!paused && document.body.contains(rail)) ygRecsGo(rail, 1);
    }
    function start() {
      if (timer) return;
      timer = window.setInterval(tick, YG_RECS_INTERVAL);
    }
    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }
    rail.__ygAutoplayRestart = function () { stop(); start(); };

    // Pause on interaction, resume when it ends.
    ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach(function (ev) {
      rail.addEventListener(ev, function () { paused = true; }, { passive: true });
    });
    ['mouseleave', 'focusout', 'touchend'].forEach(function (ev) {
      rail.addEventListener(ev, function () { paused = false; }, { passive: true });
    });
    // Pause when the tab is hidden (don't scroll in the background).
    document.addEventListener('visibilitychange', function () {
      paused = document.hidden;
    });

    start();
  }

  /* Hide slides for products already in the cart (and re-hide after adding
     one from the slider). Reads product ids from the cart payload; falls
     back to fetching /cart.js when none was supplied. If every slide gets
     hidden, the whole section is hidden. Runs after each re-render. */
  function ygApplyRecsInCart(ids) {
    var recs = document.querySelector('cart-drawer [data-yg-cart-recs]');
    if (!recs) return;
    // Only the ORIGINAL slides — never the loop clones (those mirror whatever
    // the originals end up showing after this filter runs).
    var slides = recs.querySelectorAll('.yg-cart-recs__slide:not([data-clone])');
    if (!slides.length) return;
    var idSet = {};
    (ids || []).forEach(function (id) { idSet[String(id)] = true; });
    var visible = 0;
    slides.forEach(function (slide) {
      var pid = slide.getAttribute('data-product-id');
      var inCart = pid && idSet[pid];
      slide.hidden = !!inCart;
      if (!inCart) visible++;
    });
    recs.hidden = visible === 0;
  }

  function ygFilterRecsInCart(cartData) {
    if (cartData && Array.isArray(cartData.items)) {
      ygApplyRecsInCart(cartData.items.map(function (it) { return it.product_id; }));
      return;
    }
    var cartUrl2 = (typeof routes !== 'undefined' && routes && routes.cart_url) || '/cart';
    fetch(cartUrl2 + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        ygApplyRecsInCart(data && data.items ? data.items.map(function (it) { return it.product_id; }) : []);
      })
      .catch(function () {});
  }

  // Init on load and after every drawer re-render (rail is a fresh node then).
  function ygInitRecs() {
    ygFilterRecsInCart(null);
    // Autoplay + progress UI deferred a frame so hidden slides settle first.
    requestAnimationFrame(function () {
      ygInitRecsUI();
      ygInitRecsAutoplay();
    });
  }
  if (document.readyState !== 'loading') requestAnimationFrame(ygInitRecs);
  document.addEventListener('DOMContentLoaded', function () { requestAnimationFrame(ygInitRecs); });

  /* CRUCIAL: the rail has zero width until the drawer is actually open, so an
     autoplay init that ran on load/re-render saw no overflow and bailed
     (leaving the slider static). Watch <cart-drawer> for the .active class
     Dawn adds on open, and (re)init once it's visible and the rail has real
     dimensions. */
  (function watchDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () {
      if (drawer.classList.contains('active')) {
        // Two frames: let the open transition lay the rail out first.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            ygInitRecsUI();
            ygInitRecsAutoplay();
          });
        });
      }
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ------------------------------------------------------------------------
     Cart-line delete: play a collapse animation on the row BEFORE handing
     off to Dawn's own removal flow. <cart-remove-button> attaches its own
     click listener in its constructor (upgraded on every re-render, since
     the drawer swaps innerHTML), so a normal bubble-phase delegated
     listener here would run alongside it, not before it. Listening on the
     CAPTURE phase and calling stopImmediatePropagation() lets us block that
     built-in listener, animate, then call updateQuantity ourselves once the
     animation finishes. ------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('cart-drawer .cart-remove-button');
    if (!trigger) return;
    var row = trigger.closest('.cart-item');
    if (!row || row.classList.contains('is-removing')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();

    var removeButton = trigger.closest('cart-remove-button');
    var cartItems = removeButton && (removeButton.closest('cart-items') || removeButton.closest('cart-drawer-items'));
    if (!cartItems || !removeButton) return;

    // Remember this product so "Buy it Again" can offer it back later
    // (works for guests too, unlike server-side purchase history).
    ygRememberRemovedProduct(row);

    var lineIndex = removeButton.dataset.index;
    row.classList.add('is-removing');

    // Run the actual removal exactly ONCE. .is-removing transitions two
    // properties (opacity + transform), so 'transitionend' fires twice;
    // the setTimeout is a fallback if neither fires. Without this guard,
    // updateQuantity got called 2–3× for the same line — the first removes
    // it, the later calls hit a now-shifted/absent index and surface Dawn's
    // "error while updating your cart". A synthetic event whose
    // currentTarget is the <cart-remove-button> is passed so Dawn's
    // eventTarget === 'clear' branch (instanceof CartRemoveButton) is taken.
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      cartItems.updateQuantity(lineIndex, 0, { currentTarget: removeButton });
    };

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      finish();
      return;
    }

    // Wait for the HEIGHT collapse specifically (the longest of the animated
    // properties) so rows below have finished gliding up before the line is
    // actually removed — otherwise the DOM removal cuts the collapse short
    // and the rows snap. setTimeout is a safety net if the event misfires.
    row.addEventListener('transitionend', function onEnd(ev) {
      if (ev.propertyName && ev.propertyName !== 'max-height') return;
      row.removeEventListener('transitionend', onEnd);
      finish();
    });
    window.setTimeout(finish, 420);
  }, true);

  /* ------------------------------------------------------------------------
     Cart-line subscribe/one-time plan switcher. Dawn has no built-in way to
     change an existing line's selling plan — /cart/add.js only sets it at
     add-to-cart time — so this posts straight to /cart/change.js with
     { line, selling_plan }, matching Shopify's documented cart line-update
     shape (selling_plan: null clears back to one-time). Re-uses the same
     cart.renderContents() re-render path as the "+ Add" handler above so
     price/subtotal/free-shipping bar all stay in sync from one response.
     ------------------------------------------------------------------------ */
  function closeAllPlanMenus(except) {
    document.querySelectorAll('[data-yg-cart-plan-menu]:not([hidden])').forEach(function (menu) {
      if (menu === except) return;
      menu.hidden = true;
      var wrap = menu.closest('[data-yg-cart-plan]');
      var trigger = wrap && wrap.querySelector('[data-yg-cart-plan-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-yg-cart-plan-trigger]');
    if (trigger) {
      e.preventDefault();
      var wrap = trigger.closest('[data-yg-cart-plan]');
      var menu = wrap.querySelector('[data-yg-cart-plan-menu]');
      var isOpen = !menu.hidden;
      closeAllPlanMenus();
      menu.hidden = isOpen;
      trigger.setAttribute('aria-expanded', String(!isOpen));
      return;
    }

    var option = e.target.closest('[data-yg-cart-plan-option]');
    if (option) {
      e.preventDefault();
      var planWrap = option.closest('[data-yg-cart-plan]');
      var planTrigger = planWrap.querySelector('[data-yg-cart-plan-trigger]');
      var planMenu = planWrap.querySelector('[data-yg-cart-plan-menu]');
      if (planTrigger.classList.contains('is-loading')) return;

      var lineKey = planWrap.getAttribute('data-line-key');
      var lineIndex = planWrap.getAttribute('data-line');
      var sellingPlanId = option.getAttribute('data-selling-plan');

      planMenu.hidden = true;
      planTrigger.setAttribute('aria-expanded', 'false');
      planTrigger.classList.add('is-loading');
      planTrigger.setAttribute('aria-busy', 'true');

      var cart = getCart();
      // Prefer the stable line KEY over the positional index. The index can
      // point at the wrong line once a plan change makes Shopify re-order or
      // merge lines; the key (variant:properties hash) never does. Only the
      // id and selling_plan go in the body — NEVER quantity — so this call
      // can't accidentally change how many units are in the line.
      var body = { selling_plan: sellingPlanId ? Number(sellingPlanId) : null };
      if (lineKey) {
        body.id = lineKey;
      } else {
        body.line = Number(lineIndex);
      }
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      fetch((window.routes ? window.routes.cart_change_url : '/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) {
            // Shopify rejected the change (e.g. plan no longer valid).
            // Restore the trigger instead of hard-reloading the page.
            planTrigger.classList.remove('is-loading');
            planTrigger.removeAttribute('aria-busy');
            return;
          }
          if (cart && cart.renderContents && res.sections) {
            cart.renderContents(res);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'yg-cart-plan', cartData: res });
            }
            if (window.YGUpdateCartCount) window.YGUpdateCartCount(res.item_count);
          } else {
            window.location = window.routes ? window.routes.cart_url : '/cart';
          }
        })
        .catch(function () {
          planTrigger.classList.remove('is-loading');
          planTrigger.removeAttribute('aria-busy');
        });
      return;
    }

    if (!e.target.closest('[data-yg-cart-plan]')) closeAllPlanMenus();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllPlanMenus();
  });
})();
